import { api } from './api'

export type TriState = 'Unknown' | 'Yes' | 'No'

export interface DeliveryArea {
  id: number
  name: string
  customerCount: number
  isDeleted: boolean
}

export interface CustomerListItem {
  id: number
  firstName: string
  lastName: string | null
  homePhone: string | null
  mobilePhone: string | null
  deliveryAreaId: number | null
  deliveryAreaName: string
  isActive: boolean
  isDeleted: boolean
}

export interface Customer {
  id: number
  deliveryAreaId: number
  deliveryAreaName: string
  sortOrder: number
  firstName: string
  lastName: string | null
  homePhone: string | null
  mobilePhone: string | null
  workPhone: string | null
  email: string | null
  deliveryStreet: string
  deliveryPostalCode: string | null
  deliveryCity: string | null
  deliveryNote: string | null
  ringNote: string | null
  doNotContactUntil: string | null
  organicPreference: TriState
  invoiceByEmail: TriState
  wantsOrderEmail: boolean
  smsOptOut: boolean
  onlineOrderOptOut: boolean
  isActive: boolean
  accessKey: string
  mobilePhoneVerifiedUtc: string | null
  mobilePhoneChangedUtc: string | null
  emailVerifiedUtc: string | null
}

export type CustomerInput = Omit<Customer, 'id' | 'deliveryAreaName' | 'accessKey' | 'mobilePhoneVerifiedUtc' | 'mobilePhoneChangedUtc' | 'emailVerifiedUtc'>

export const deliveryAreasApi = {
  list: (includeDeleted = false) =>
    api.get<DeliveryArea[]>(`/api/v1/delivery-areas${includeDeleted ? '?includeDeleted=true' : ''}`),
  get: (id: number) => api.get<DeliveryArea>(`/api/v1/delivery-areas/${id}`),
  create: (name: string) => api.post<DeliveryArea>('/api/v1/delivery-areas', { name }),
  update: (id: number, name: string) => api.put<DeliveryArea>(`/api/v1/delivery-areas/${id}`, { name }),
  remove: (id: number) => api.del(`/api/v1/delivery-areas/${id}`),
  restore: (id: number) => api.post<void>(`/api/v1/delivery-areas/${id}/restore`, {}),
}

export interface DeliveryOccasion {
  id: number
  deliveryAreaId: number
  date: string
  isClosed: boolean
}

export type WeekInterval = 'EveryWeek' | 'EverySecondWeek' | 'EveryThirdWeek' | 'EveryFourthWeek'

export const occasionsApi = {
  list: (areaId: number, upcomingOnly = false) =>
    api.get<DeliveryOccasion[]>(`/api/v1/delivery-areas/${areaId}/occasions${upcomingOnly ? '?upcomingOnly=true' : ''}`),
  createSingle: (areaId: number, date: string) =>
    api.post<DeliveryOccasion>(`/api/v1/delivery-areas/${areaId}/occasions`, { date }),
  createRecurring: (areaId: number, body: { startDate: string; endDate: string; interval: WeekInterval }) =>
    api.post<DeliveryOccasion[]>(`/api/v1/delivery-areas/${areaId}/occasions/recurring`, body),
  remove: (areaId: number, occasionId: number) =>
    api.del(`/api/v1/delivery-areas/${areaId}/occasions/${occasionId}`),
}

export const customersApi = {
  list: (params: { deliveryAreaId?: number; active?: boolean; query?: string; includeDeleted?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (params.deliveryAreaId != null) q.set('deliveryAreaId', String(params.deliveryAreaId))
    if (params.active != null) q.set('active', String(params.active))
    if (params.query) q.set('query', params.query)
    if (params.includeDeleted) q.set('includeDeleted', 'true')
    const qs = q.toString()
    return api.get<CustomerListItem[]>(`/api/v1/customers${qs ? `?${qs}` : ''}`)
  },
  get: (id: number) => api.get<Customer>(`/api/v1/customers/${id}`),
  create: (input: CustomerInput) => api.post<Customer>('/api/v1/customers', input),
  update: (id: number, input: CustomerInput) => api.put<Customer>(`/api/v1/customers/${id}`, input),
  remove: (id: number) => api.del(`/api/v1/customers/${id}`),
  restore: (id: number) => api.post<void>(`/api/v1/customers/${id}/restore`, {}),
  resetPhoneVerification: (id: number, clearNumber = false) =>
    api.post<void>(`/api/v1/customers/${id}/reset-phone-verification${clearNumber ? '?clearNumber=true' : ''}`, {}),
  resetEmailVerification: (id: number) =>
    api.post<void>(`/api/v1/customers/${id}/reset-email-verification`, {}),
  move: (customerIds: number[], targetDeliveryAreaId: number) =>
    api.post<{ moved: number }>('/api/v1/customers/move', { customerIds, targetDeliveryAreaId }),
}

export const setCustomerOrder = (areaId: number, customerIds: number[]) =>
  api.put<void>(`/api/v1/delivery-areas/${areaId}/customer-order`, { customerIds })

export interface ProductListItem {
  id: number
  name: string
  availableFrom: string
  availableUntil: string | null
  currentPrice: number | null
  isAvailableToday: boolean
}

export interface ProductPrice {
  id: number
  validFrom: string
  amount: number
}

export interface Product {
  id: number
  name: string
  availableFrom: string
  availableUntil: string | null
  currentPrice: number | null
  prices: ProductPrice[]
}

export const productsApi = {
  list: (availableOnly = false) =>
    api.get<ProductListItem[]>(`/api/v1/products${availableOnly ? '?availableOnly=true' : ''}`),
  get: (id: number) => api.get<Product>(`/api/v1/products/${id}`),
  create: (body: { name: string; availableFrom: string; availableUntil: string | null; initialPrice: number }) =>
    api.post<Product>('/api/v1/products', body),
  update: (id: number, body: { name: string; availableFrom: string; availableUntil: string | null }) =>
    api.put<Product>(`/api/v1/products/${id}`, body),
  remove: (id: number) => api.del(`/api/v1/products/${id}`),
}

export const pricesApi = {
  create: (productId: number, body: { validFrom: string; amount: number }) =>
    api.post<ProductPrice>(`/api/v1/products/${productId}/prices`, body),
  update: (productId: number, priceId: number, body: { validFrom: string; amount: number }) =>
    api.put<ProductPrice>(`/api/v1/products/${productId}/prices/${priceId}`, body),
  remove: (productId: number, priceId: number) =>
    api.del(`/api/v1/products/${productId}/prices/${priceId}`),
}

export type OrderStatus = 'Open' | 'Closed' | 'Invoiced' | 'Cancelled'

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  Open: 'Öppen', Closed: 'Stängd', Invoiced: 'Fakturerad', Cancelled: 'Makulerad',
}

export interface OrderRow {
  id: number
  productId: number
  productName: string
  numberOfUnits: number
  unitPrice: number
  lineTotal: number
}

export interface Order {
  id: number
  customerId: number
  customerName: string
  deliveryOccasionId: number
  occasionDate: string
  status: OrderStatus
  comment: string | null
  total: number
  rows: OrderRow[]
}

export interface OrderListItem {
  id: number
  deliveryOccasionId: number
  occasionDate: string
  status: OrderStatus
  rowCount: number
  total: number
}

export interface OrderRowInput { productId: number; numberOfUnits: number }

export const ordersApi = {
  list: (params: { customerId?: number; deliveryOccasionId?: number; status?: OrderStatus } = {}) => {
    const q = new URLSearchParams()
    if (params.customerId != null) q.set('customerId', String(params.customerId))
    if (params.deliveryOccasionId != null) q.set('deliveryOccasionId', String(params.deliveryOccasionId))
    if (params.status) q.set('status', params.status)
    const qs = q.toString()
    return api.get<OrderListItem[]>(`/api/v1/orders${qs ? `?${qs}` : ''}`)
  },
  get: (id: number) => api.get<Order>(`/api/v1/orders/${id}`),
  place: (body: { customerId: number; deliveryOccasionId: number; comment: string | null; rows: OrderRowInput[] }) =>
    api.post<Order>('/api/v1/orders', body),
  update: (id: number, body: { comment: string | null; rows: OrderRowInput[] }) =>
    api.put<Order>(`/api/v1/orders/${id}`, body),
  cancel: (id: number) => api.post<void>(`/api/v1/orders/${id}/cancel`, {}),
}

// Multisoft web-order passthrough (forwards to Multisoft, stores locally).
export interface MultisoftResult {
  errorMessage: string | null
  success: boolean
  order: {
    customerIdWsOrder: string | null
    orderId: number
    orderStatus: string | null
    deliveryRegion: string | null
    address: string | null
    firstName: string | null
    lastName: string | null
    email: string | null
    dateOccasion: string | null
    orderRows: { orderRowId: number; productId: number; productName: string | null; numberOfUnits: number; maxNumberOfUnits: number; price: number }[] | null
  } | null
}

export const webOrdersApi = {
  get: (customerIdentifier: string) =>
    api.get<MultisoftResult>(`/api/v1/web-orders/${encodeURIComponent(customerIdentifier)}`),
  snapshot: (customerIdentifier: string) =>
    api.get<unknown>(`/api/v1/web-orders/${encodeURIComponent(customerIdentifier)}/snapshot`),
}

// --- sync failures (Multisoft passthrough → local CRM, best-effort) ---
export interface SyncFailure {
  id: number
  entity: string
  externalKey: string | null
  customerIdWsOrder: string | null
  operation: string
  error: string
  createdUtc: string
  isResolved: boolean
  resolvedUtc: string | null
}

export const syncFailuresApi = {
  list: (includeResolved = false) =>
    api.get<SyncFailure[]>(`/api/v1/sync-failures?includeResolved=${includeResolved}`),
  dismiss: (id: number) => api.post<void>(`/api/v1/sync-failures/${id}/dismiss`, {}),
}

// --- delivery + picking list (§6.19) ---
export interface PickingListItem { productId: number; productName: string; totalUnits: number }
export interface DeliveryRow { productId: number; productName: string; numberOfUnits: number }
export interface DeliveryStop {
  customerId: number
  sortOrder: number
  customerName: string
  deliveryStreet: string
  deliveryPostalCode: string | null
  deliveryCity: string | null
  homePhone: string | null
  mobilePhone: string | null
  workPhone: string | null
  deliveryNote: string | null
  hasPaperInvoice: boolean
  rows: DeliveryRow[]
}
export interface DeliveryList {
  occasionId: number
  date: string
  deliveryAreaId: number
  deliveryAreaName: string
  isClosed: boolean
  pickingList: PickingListItem[]
  stops: DeliveryStop[]
}

export const deliveryListApi = {
  get: (deliveryOccasionId: number) =>
    api.get<DeliveryList>(`/api/v1/delivery-occasions/${deliveryOccasionId}/delivery-list`),
  pdfPath: (deliveryOccasionId: number) =>
    `/api/v1/delivery-occasions/${deliveryOccasionId}/delivery-list/pdf`,
}

// --- ring list / telemarketing (§6.20-6.21) ---
export interface RingListItem {
  customerId: number
  firstName: string
  lastName: string | null
  homePhone: string | null
  mobilePhone: string | null
  workPhone: string | null
  ringNote: string | null
  balance: number
  failedAttempts: number
}

export const ringListApi = {
  list: (deliveryOccasionId: number) =>
    api.get<RingListItem[]>(`/api/v1/ring-list?deliveryOccasionId=${deliveryOccasionId}`),
  registerAttempt: (customerId: number, deliveryOccasionId: number) =>
    api.post<{ failedAttempts: number; removedFromList: boolean }>(
      '/api/v1/ring-list/attempt', { customerId, deliveryOccasionId }),
  registerNegative: (customerId: number, deliveryOccasionId: number) =>
    api.post<{ negativeResponseStreak: number; autoInactivated: boolean }>(
      '/api/v1/ring-list/negative', { customerId, deliveryOccasionId }),
}

// --- invoicing (§6.7-6.13) ---
export type InvoiceStatus = 'Unpaid' | 'PartiallyPaid' | 'Paid' | 'Cancelled'
export type InvoiceRowType = 'Product' | 'Credit' | 'Reminder' | 'Fee'
export type PaymentType = 'ManualPayment' | 'ManualAdjustment'

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  Unpaid: 'Obetald', PartiallyPaid: 'Delbetald', Paid: 'Betald', Cancelled: 'Makulerad',
}
export const INVOICE_ROW_TYPE_LABEL: Record<InvoiceRowType, string> = {
  Product: 'Produkt', Credit: 'Tillgodo', Reminder: 'Påminnelse', Fee: 'Faktureringsavgift',
}

export interface InvoiceRow {
  id: number; type: InvoiceRowType; description: string; quantity: number | null
  unitPrice: number | null; orderRowId: number | null; invoiceHeadIdToRemind: number | null; amount: number
}
export interface Invoice {
  id: number; customerId: number; customerName: string; deliveryOccasionId: number | null
  invoiceDate: string; dueDate: string; deliveryMethod: 'Email' | 'Paper'; status: InvoiceStatus
  ocrNumber: string | null; total: number; paid: number; remaining: number; rows: InvoiceRow[]
}
export interface InvoiceListItem {
  id: number; customerId: number; customerName: string; invoiceDate: string; dueDate: string
  status: InvoiceStatus; total: number; remaining: number
}

export const invoicesApi = {
  list: (params: { customerId?: number; status?: InvoiceStatus; unpaidOnly?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (params.customerId != null) q.set('customerId', String(params.customerId))
    if (params.status) q.set('status', params.status)
    if (params.unpaidOnly) q.set('unpaidOnly', 'true')
    const qs = q.toString()
    return api.get<InvoiceListItem[]>(`/api/v1/invoices${qs ? `?${qs}` : ''}`)
  },
  get: (id: number) => api.get<Invoice>(`/api/v1/invoices/${id}`),
  generate: (deliveryOccasionId: number) =>
    api.post<{ created: number; invoiceIds: number[] }>(`/api/v1/invoices/generate?deliveryOccasionId=${deliveryOccasionId}`, {}),
  pdfPath: (id: number) => `/api/v1/invoices/${id}/pdf`,
  batchPdfPath: (deliveryOccasionId: number) => `/api/v1/invoices/pdf?deliveryOccasionId=${deliveryOccasionId}`,
}

export interface LedgerInvoice { id: number; invoiceDate: string; dueDate: string; status: InvoiceStatus; total: number; paid: number; remaining: number }
export interface Payment { id: number; type: PaymentType; amount: number; date: string; invoiceId: number | null; comment: string | null }
export interface CustomerLedger { customerId: number; balance: number; invoices: LedgerInvoice[]; payments: Payment[] }

export const ledgerApi = {
  get: (customerId: number) => api.get<CustomerLedger>(`/api/v1/customers/${customerId}/ledger`),
  addPayment: (customerId: number, body: { type: PaymentType; amount: number; date: string; invoiceId: number | null; comment: string | null }) =>
    api.post<Payment>(`/api/v1/customers/${customerId}/payments`, body),
}

export interface AppSetting { key: string; value: string; description: string | null }
export const settingsApi = {
  list: () => api.get<AppSetting[]>('/api/v1/settings'),
  update: (key: string, value: string) => api.put<AppSetting>(`/api/v1/settings/${key}`, { value }),
}

export const emptyCustomer = (deliveryAreaId = 0): CustomerInput => ({
  deliveryAreaId,
  sortOrder: 0,
  firstName: '',
  lastName: '',
  homePhone: '',
  mobilePhone: '',
  workPhone: '',
  email: '',
  deliveryStreet: '',
  deliveryPostalCode: '',
  deliveryCity: '',
  deliveryNote: '',
  ringNote: '',
  doNotContactUntil: null,
  organicPreference: 'Unknown',
  invoiceByEmail: 'Unknown',
  wantsOrderEmail: false,
  smsOptOut: false,
  onlineOrderOptOut: false,
  isActive: true,
})
